import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, Home, Calculator, RotateCcw } from 'lucide-react';
import Layout from '../../../../components/Layout';
import { Button } from '../../../../components/ui/Button';

const initialValues = { voltage: '', impedance: '', ifc: '' };

const SQRT3 = Math.sqrt(3);
const UK_LINE_VOLTAGE = 400;

function InputRow({ label, placeholder, value, onChange }) {
  return (
    <div className="flex items-center rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 overflow-hidden">
      <div className="flex shrink-0 w-24 sm:w-28 items-center px-3 py-2.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium">
        {label}
      </div>
      <input
        type="number"
        inputMode="decimal"
        min="0"
        step="0.0001"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 min-w-0 px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100 bg-transparent border-0 focus:outline-none placeholder:text-gray-500 dark:placeholder:text-gray-400"
        aria-label={label}
      />
    </div>
  );
}

export default function PFCCalc() {
  const location = useLocation();
  const isMobile = location.pathname.startsWith('/tenant/mobile');

  const [values, setValues] = useState(initialValues);
  const [results, setResults] = useState({ singlePhase: null, threePhase: null });

  const backPath = isMobile ? '/tenant/mobile/calculators' : '/tenant/tools/calculators';

  const updateValue = (field, val) => {
    setValues((p) => ({ ...p, [field]: val }));
    setResults({ singlePhase: null, threePhase: null });
  };

  const countFilled = () => {
    return ['voltage', 'impedance', 'ifc'].filter((f) => values[f] !== '' && values[f] != null).length;
  };

  const handleCalculate = () => {
    const filled = countFilled();
    if (filled !== 2) return;

    let voltage = parseFloat(values.voltage);
    let impedance = parseFloat(values.impedance);
    let ifcKA = parseFloat(values.ifc);

    // Derive the missing value using single-phase: Ifc (A) = Uo / Z
    if (values.voltage !== '' && values.impedance !== '') {
      // Have Uo and Z - no need to derive
    } else if (values.voltage !== '' && values.ifc !== '') {
      const ifcA = ifcKA * 1000;
      impedance = voltage / ifcA;
    } else if (values.impedance !== '' && values.ifc !== '') {
      const ifcA = ifcKA * 1000;
      voltage = impedance * ifcA;
    }

    if (!voltage || !impedance || impedance <= 0) return;

    // Single phase: I = Uo / Z (L-N fault)
    const singlePhaseAmps = voltage / impedance;
    const singlePhaseKA = singlePhaseAmps / 1000;

    // Three phase: I = U / (√3 × Z) where U = 400V line-line
    const threePhaseAmps = UK_LINE_VOLTAGE / (SQRT3 * impedance);
    const threePhaseKA = threePhaseAmps / 1000;

    setResults({
      singlePhase: { kA: singlePhaseKA, amps: singlePhaseAmps },
      threePhase: { kA: threePhaseKA, amps: threePhaseAmps },
    });

    setValues((p) => ({
      ...p,
      ...(values.voltage === '' && { voltage: String(voltage.toFixed(2)) }),
      ...(values.impedance === '' && { impedance: String(impedance.toFixed(4)) }),
      ...(values.ifc === '' && { ifc: String(singlePhaseKA.toFixed(4)) }),
    }));
  };

  const handleReset = () => {
    setValues({ ...initialValues });
    setResults({ singlePhase: null, threePhase: null });
  };

  const canCalculate = countFilled() === 2;

  const formatResult = (val) => {
    if (val == null) return 'N/A';
    if (val >= 1000) return val.toFixed(2);
    if (val >= 1) return val.toFixed(3);
    return val.toFixed(4);
  };

  const content = (
    <div className="max-w-2xl mx-auto">
      {/* Header bar */}
      <div className="flex items-center justify-between bg-green-600 dark:bg-green-700 px-4 py-3 rounded-t-lg -mx-4 -mt-4 mb-0 sm:rounded-t-none sm:mx-0 sm:mt-0 sm:-mx-0 sm:-mt-0 sm:rounded-lg">
        <Link
          to={backPath}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors"
          aria-label="Back"
        >
          <ChevronLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-lg font-semibold text-white">PFC Calculator</h1>
        <Link
          to={isMobile ? '/tenant/mobile' : '/tenant/dashboard'}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors"
          aria-label="Home"
        >
          <Home className="h-5 w-5" />
        </Link>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400 mt-4 mb-4 text-center">
        Enter any two values
      </p>

      <div className={`space-y-4 ${isMobile ? 'pb-8' : ''}`}>
        <InputRow
          label="Voltage"
          placeholder="Uo"
          value={values.voltage}
          onChange={(val) => updateValue('voltage', val)}
        />
        <InputRow
          label="Ze / Zs"
          placeholder="Impedance"
          value={values.impedance}
          onChange={(val) => updateValue('impedance', val)}
        />
        <InputRow
          label="Ifc PFC"
          placeholder="Fault Current kA"
          value={values.ifc}
          onChange={(val) => updateValue('ifc', val)}
        />

        {/* Results table */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
          <div className="bg-green-600 dark:bg-green-700 px-4 py-2 flex items-center">
            <span className="text-sm font-semibold text-white flex-1">Ifc Result</span>
            <span className="text-xs text-white/90 w-20 text-right">kA</span>
            <span className="text-xs text-white/90 w-20 text-right">Amps</span>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-600">
            <div className="grid grid-cols-[1fr_5rem_5rem] gap-2 items-center px-4 py-3 bg-white dark:bg-gray-800">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Single Phase</span>
              <span className="text-sm text-gray-900 dark:text-gray-100 tabular-nums text-right">{formatResult(results.singlePhase?.kA)}</span>
              <span className="text-sm text-gray-900 dark:text-gray-100 tabular-nums text-right">{formatResult(results.singlePhase?.amps)}</span>
            </div>
            <div className="grid grid-cols-[1fr_5rem_5rem] gap-2 items-center px-4 py-3 bg-white dark:bg-gray-800">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Three Phase</span>
              <span className="text-sm text-gray-900 dark:text-gray-100 tabular-nums text-right">{formatResult(results.threePhase?.kA)}</span>
              <span className="text-sm text-gray-900 dark:text-gray-100 tabular-nums text-right">{formatResult(results.threePhase?.amps)}</span>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleCalculate}
            disabled={!canCalculate}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Calculator className="h-4 w-4" />
            Calculate
          </Button>
          <Button
            onClick={handleReset}
            variant="destructive"
            className="flex-1 py-3 flex items-center justify-center gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
        </div>

        {/* Definitions */}
        <div className="mt-6 space-y-1 text-sm text-gray-700 dark:text-gray-300">
          <p>PSCC = L-N or L-L (TP) Fault Current</p>
          <p>PEFC = L-E Fault Current</p>
          <p>PFC = The higher of PSCC or PEFC</p>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div className="px-4 py-6 pb-8 min-h-screen bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
        {content}
      </div>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 px-4 sm:px-6 lg:px-8 py-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            PFC Calculator
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Prospective fault current calculator — enter any two values to calculate
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6">
          {content}
        </div>
      </div>
    </Layout>
  );
}
